/**
 * dashboardConfigService.js - Service per gestire le configurazioni dashboard
 * Tutte le dashboard leggono da Supabase, zero hardcoding
 */

import supabase from '../lib/supabase';

/**
 * Get dashboard configuration based on user profile
 * Fallback chain: exact match → role only → default
 */
export const getDashboardConfig = async ({ role_professional, user_type, role_system, lang = 'it' }) => {
  try {
    // 1. Try exact match
    let { data, error } = await supabase
      .from('blueprint_dashboard_config')
      .select('*')
      .eq('role_professional', role_professional)
      .eq('lang', lang)
      .eq('is_active', true)
      .single();
    
    if (data && !error) {
      console.log('📊 Dashboard config: exact match found for', role_professional);
      return { data, error: null };
    }
    
    // 2. Fallback to role_professional only (ignore user_type/role_system)
    if (error?.code === 'PGRST116') {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('blueprint_dashboard_config')
        .select('*')
        .eq('role_professional', role_professional)
        .eq('is_active', true)
        .limit(1)
        .single();
      
      if (fallbackData && !fallbackError) {
        console.log('📊 Dashboard config: fallback match found for', role_professional);
        return { data: fallbackData, error: null };
      }
    }
    
    // 3. Fallback to default
    const { data: defaultData, error: defaultError } = await supabase
      .from('blueprint_dashboard_config')
      .select('*')
      .eq('role_professional', 'default')
      .eq('is_active', true)
      .limit(1)
      .single();
    
    if (defaultData && !defaultError) {
      console.log('📊 Dashboard config: using default config');
      return { data: defaultData, error: null };
    }
    
    // No config found at all
    console.warn('📊 Dashboard config: no config found, returning empty');
    return { 
      data: getEmptyConfig(role_professional), 
      error: null 
    };
    
  } catch (err) {
    console.error('📊 Dashboard config error:', err);
    return { data: getEmptyConfig(role_professional), error: err };
  }
};

/**
 * Get all dashboard configurations (for admin)
 */
export const getAllDashboardConfigs = async () => {
  try {
    const { data, error } = await supabase
      .from('blueprint_dashboard_config')
      .select('*')
      .order('role_professional', { ascending: true });
    
    return { data: data || [], error };
  } catch (err) {
    console.error('📊 Get all configs error:', err);
    return { data: [], error: err };
  }
};

/**
 * Get single dashboard config by ID
 */
export const getDashboardConfigById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('blueprint_dashboard_config')
      .select('*')
      .eq('id', id)
      .single();
    
    return { data, error };
  } catch (err) {
    console.error('📊 Get config by ID error:', err);
    return { data: null, error: err };
  }
};

/**
 * Update dashboard configuration
 */
export const updateDashboardConfig = async (id, payload) => {
  try {
    const { data, error } = await supabase
      .from('blueprint_dashboard_config')
      .update({
        ...payload,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  } catch (err) {
    console.error('📊 Update config error:', err);
    return { data: null, error: err };
  }
};

/**
 * Create new dashboard configuration
 */
export const createDashboardConfig = async (payload) => {
  try {
    const { data, error } = await supabase
      .from('blueprint_dashboard_config')
      .insert(payload)
      .select()
      .single();
    
    return { data, error };
  } catch (err) {
    console.error('📊 Create config error:', err);
    return { data: null, error: err };
  }
};

/**
 * Delete dashboard configuration
 */
export const deleteDashboardConfig = async (id) => {
  try {
    const { error } = await supabase
      .from('blueprint_dashboard_config')
      .delete()
      .eq('id', id);
    
    return { error };
  } catch (err) {
    console.error('📊 Delete config error:', err);
    return { error: err };
  }
};

/**
 * Toggle config active status
 */
export const toggleDashboardConfigActive = async (id, is_active) => {
  return updateDashboardConfig(id, { is_active });
};

/**
 * Returns an empty config structure (fallback when DB has no data)
 */
const getEmptyConfig = (role_professional) => ({
  id: null,
  role_professional: role_professional || 'default',
  user_type: null,
  role_system: 'user',
  lang: 'it',
  hero: {
    title: 'Welcome to',
    title_accent: 'SlabReality',
    title_suffix: '',
    subtitle: 'Configure your dashboard in Blueprint CMS.',
    tagline: '',
    hero_image: null,
    cta_primary: null,
    cta_secondary: null
  },
  feature_pills: [],
  action_cards: [],
  priority_panel: [],
  widgets: [],
  checklist_items: [],
  sidebar_sections: [],
  is_active: true
});

export default {
  getDashboardConfig,
  getAllDashboardConfigs,
  getDashboardConfigById,
  updateDashboardConfig,
  createDashboardConfig,
  deleteDashboardConfig,
  toggleDashboardConfigActive
};
